// ── usePositionPnl — Shared P&L calculation hook ────────────────
// Used by both PositionTracker and ValueDashboard's PositionSidebar.
// Derives unrealized P&L from accountStore positions + marketStore prices.

import { useAccountStore } from '../../stores/accountStore';
import { useMarketStore } from '../../stores/marketStore';

export interface PositionWithPnl {
  ticker: string;
  side: 'yes' | 'no';
  position: number;
  averagePrice: number;
  currentPrice: number | null;
  unrealizedPnl: number | null;
}

export function usePositionPnl() {
  const positions = useAccountStore((s) => s.positions);
  const version = useMarketStore((s) => s.version);
  const getMarketPrice = useMarketStore((s) => s.getMarketPrice);

  // Force re-derive when Hydra prices update
  void version;

  const positionsWithPnl: PositionWithPnl[] = positions.map((pos) => {
    const currentPrice = getMarketPrice(pos.ticker);
    let unrealizedPnl: number | null = null;
    if (currentPrice !== null) {
      unrealizedPnl = pos.side === 'yes'
        ? (currentPrice - pos.averagePrice) * pos.position
        : (pos.averagePrice - currentPrice) * pos.position;
    }
    return { ...pos, currentPrice, unrealizedPnl };
  });

  const totalPnl = positionsWithPnl.reduce((sum, p) => sum + (p.unrealizedPnl ?? 0), 0);

  return { positionsWithPnl, totalPnl };
}
